// Beneficiaries — top-level list of payment beneficiaries for the current
// merchant. Reachable from BOTH admin and merchant sidebars.
//
// Why this page exists (and not just the embedded card on MerchantDetail):
//   The merchant-role sidebar has no link to /merchants/:id, so a merchant
//   user could not register a beneficiary at all — the "Add" affordance
//   was only visible on a page they had no path to. This page gives every
//   role a direct route to manage beneficiaries on the merchant currently
//   selected in the switcher.
//
// Backend contract (same endpoints MerchantDetail uses):
//   GET  /v1/merchants/:id/beneficiaries     viewer+ membership
//   POST /v1/merchants/:id/beneficiaries     operator+ membership
//
// Pagination: client-side at 25/page. Backend returns the full list today
// (no LIMIT/OFFSET). Cross ~500 rows and we add server-side paging.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { I } from "@/components/Icons";
import { AddBeneficiaryModal } from "@/components/AddBeneficiaryModal";
import { listBeneficiaries } from "@/api/beneficiaries";
import { listBankAccounts } from "@/api/bankAccounts";
import { formatDateTime } from "@/utils/format";
import { useAuth, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";

const PAGE_SIZE = 25;

export function Beneficiaries() {
  const navigate = useNavigate();
  const authStatus = useAuth((s) => s.status);
  const merchantId = useUI((s) => s.currentMerchantId);
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  // Per-merchant role lookup. A user can be operator on merchant A and
  // viewer on merchant B; we need the role for the currently-selected
  // merchant only. Mirrors the backend gate at
  // RequireMembershipRole("operator") on POST /v1/merchants/:id/beneficiaries.
  const myMembershipRole = useAuth((s) =>
    s.user?.memberships.find((m) => m.merchant_id === merchantId)?.role,
  );
  const canManage =
    isAdmin || myMembershipRole === "owner" || myMembershipRole === "operator";

  const [page, setPage] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  const enabled = authStatus === "authenticated" && merchantId != null;

  const beneQuery = useQuery({
    queryKey: ["beneficiaries", merchantId],
    queryFn: () => listBeneficiaries(merchantId!),
    enabled,
    staleTime: 60_000,
  });
  // Rails are needed for the Add modal's "Register via rail" select. Pre-load
  // on page mount so the modal opens populated without a network race.
  const railsQuery = useQuery({
    queryKey: ["bank-accounts", merchantId],
    queryFn: () => listBankAccounts(merchantId!),
    enabled,
    staleTime: 60_000,
  });

  const beneficiaries = beneQuery.data ?? [];
  const rails = railsQuery.data ?? [];

  const totalPages = Math.max(1, Math.ceil(beneficiaries.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * PAGE_SIZE;
  const visible = beneficiaries.slice(start, start + PAGE_SIZE);
  const showPager = beneficiaries.length > PAGE_SIZE;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console</div>
          <h1>Beneficiaries</h1>
          <div className="sub">
            Payees registered with the payment provider. Required before a
            payout can be sent.
          </div>
        </div>
        <div className="actions">
          {canManage && merchantId != null && rails.length > 0 && (
            <button
              className="btn accent"
              type="button"
              onClick={() => setAddOpen(true)}
            >
              <I.Plus size={12} /> Add beneficiary
            </button>
          )}
        </div>
      </div>

      {merchantId == null ? (
        // No merchant selected — admins land here before picking one in the
        // switcher. The Payouts page uses the same pattern (audit-pass-3
        // "honest empty state" requirement).
        <div className="card flat">
          <div className="empty">
            <div className="icon">
              <I.Users size={16} />
            </div>
            <h3>Pick a merchant</h3>
            <p>
              Beneficiaries are scoped to one merchant at a time. Use the
              switcher at the top of the sidebar.
            </p>
          </div>
        </div>
      ) : beneQuery.isLoading ? (
        <div className="card flat">
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading beneficiaries…
            </div>
          </div>
        </div>
      ) : beneQuery.isError ? (
        <div className="card flat">
          <div className="empty">
            <p>Couldn't load beneficiaries.</p>
          </div>
        </div>
      ) : beneficiaries.length === 0 ? (
        <div className="card flat">
          <div className="empty">
            <div className="icon">
              <I.Users size={16} />
            </div>
            <h3>No beneficiaries yet</h3>
            {/* Two paths: rails missing first, OR rails present but no
                bene registered yet. Surface the right next step rather
                than a generic empty. */}
            {rails.length === 0 ? (
              <p>
                Register a bank rail first — beneficiaries are tied to one
                rail's provider credentials. Once a rail is verified, the
                "Add beneficiary" button appears here.
              </p>
            ) : canManage ? (
              <>
                <p>
                  Add your first beneficiary to start sending payouts to this
                  payee from the connected rail.
                </p>
                <button
                  className="btn primary"
                  type="button"
                  style={{ marginTop: 12 }}
                  onClick={() => setAddOpen(true)}
                >
                  <I.Plus size={12} /> Add beneficiary
                </button>
              </>
            ) : (
              <p>
                Only operators and owners can register beneficiaries — ask
                a team member with the right access to add one.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="card flat">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Account</th>
                <th>IFSC</th>
                <th>Registered</th>
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((b) => (
                // Clickable row → /beneficiaries/:id. Cursor + hover
                // bg make it feel selectable. Keyboard activation via
                // Enter/Space mirrors the role="button" semantics.
                <tr
                  key={b.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/beneficiaries/${b.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/beneficiaries/${b.id}`);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                  title={`Open ${b.label}`}
                >
                  <td>
                    <div className="cell-name">{b.label}</div>
                    {/* The bare integer matches the CSV beneficiary_id
                        column. Operators copy this directly into the
                        upload template — NOT a "ben_123" prefix string. */}
                    <div className="cell-id">id: {b.id}</div>
                  </td>
                  <td className="mono">{b.account_no}</td>
                  <td className="mono">{b.ifsc}</td>
                  <td className="dim">{b.is_registered ? "Yes" : "No"}</td>
                  {/* The list endpoint doesn't return created_at today;
                      surface "—" until the backend adds it. The column
                      slot exists so adding the field later doesn't
                      reshuffle every other header. */}
                  <td className="dim">
                    {"created_at" in b && typeof b.created_at === "string"
                      ? formatDateTime(b.created_at)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {showPager && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 8,
                padding: "8px 12px",
                borderTop: "1px solid var(--hair)",
                fontSize: 12,
                color: "var(--ink-2)",
              }}
            >
              <span className="dim">
                Showing {start + 1}–
                {Math.min(start + PAGE_SIZE, beneficiaries.length)} of{" "}
                {beneficiaries.length}
              </span>
              <button
                className="btn ghost sm"
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
              >
                <I.ChevronLeft size={11} /> Prev
              </button>
              <button
                className="btn ghost sm"
                type="button"
                onClick={() =>
                  setPage((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={safePage >= totalPages - 1}
              >
                Next <I.ChevronRight size={11} />
              </button>
            </div>
          )}
        </div>
      )}

      {canManage && merchantId != null && (
        <AddBeneficiaryModal
          merchantId={merchantId}
          rails={rails}
          open={addOpen}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}
